import api from '../../services/api';

/*
 * Receiving: the merchandise coming into the warehouse.
 *
 * Two different acts on the same order, and not done by the same people:
 *
 *   declaring   how much is coming. The client does it (it is its goods) or the
 *               operator on its behalf. It does NOT move stock: it is an
 *               expectation.
 *   confirming  how much really arrived, box in hand. Only whoever operates the
 *               space. It is the only thing that writes to the kardex, and it
 *               lands in a receiving zone, where nothing is sellable yet.
 *
 * The server sends `can_confirm` with the list, so no screen works out who is who.
 */

/**
 * @param {object} params - `warehouse, company, status, search, page,
 *   page_size`. The answer carries `can_confirm` and `status_counts`, which
 *   cover every status over the other filters, not the page.
 */
export const getReceiptsRequest = async (params) => {
  return await api.get('inventories/receipts/', { params });
};

/** One order with its `lines` (and the flags of each product) and its `events`. */
export const getReceiptRequest = async (receiptId) => {
  return await api.get(`inventories/receipts/${receiptId}/`);
};

/**
 * Declares an order. No location: it is picked when the load is received.
 *
 * @param {object} payload - `warehouse_id, company_id, supplier, reference,
 *   notes, lines: [{product_id, expected, lot, expiry_date}]`
 */
export const createReceiptRequest = async (payload) => {
  return await api.post('inventories/receipts/create/', payload);
};

/** Only while it is pending: the server refuses once anything was counted. */
export const updateReceiptRequest = async (receiptId, payload) => {
  return await api.patch(`inventories/receipts/${receiptId}/`, payload);
};

/**
 * Confirms what arrived NOW; it adds up over what was already counted.
 *
 * @param {number} receiptId
 * @param {{counts: Array<{line_id: number, quantity: number, lot: string,
 *   expiry_date: string|null}>, slot_id: number|null}} payload
 */
export const receiveReceiptRequest = async (receiptId, payload) => {
  return await api.post(`inventories/receipts/${receiptId}/receive/`, payload);
};

/** Closes with a shortage: what did not arrive stays written and no more is received. */
export const closeReceiptRequest = async (receiptId) => {
  return await api.post(`inventories/receipts/${receiptId}/close/`);
};

/**
 * Voids the order, reverting what was received — only while it is still whole
 * in the receiving zone. Nothing is deleted.
 */
export const cancelReceiptRequest = async (receiptId, reason = '') => {
  return await api.post(`inventories/receipts/${receiptId}/cancel/`, { reason });
};

/**
 * Several orders out of one sheet, for one company in one warehouse. All or
 * nothing: the server names the row and the reference that failed.
 *
 * @param {FormData} formData - `warehouse_id`, `company_id`, `excel_file`
 */
export const uploadReceiptsRequest = async (formData) => {
  return await api.post('inventories/receipts/bulk/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/** The printable manifest, as a PDF. */
export const getReceiptManifestRequest = async (receiptId) => {
  return await api.get(`inventories/receipts/${receiptId}/manifest/`, { responseType: 'blob' });
};
